import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Grid,
  Box,
  Typography,
  Alert,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus, IconUpload, IconDownload } from "@tabler/icons-react";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import useContractPermissions from "hooks/useContractPermissions";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const ProfessionsList = () => {
  const { canImportData } = useContractPermissions();
  const [professions, setProfessions] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProfession, setCurrentProfession] = useState({
    code: "",
    name: "",
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [openImportModal, setOpenImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [codeError, setCodeError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Função para formatar o código no formato 9999-99
  const formatCode = (code) => {
    if (!code) return "";
    // Remove qualquer formatação existente e mantém apenas números
    const numbersOnly = code.replace(/\D/g, "");
    // Aplica o formato 9999-99
    if (numbersOnly.length >= 6) {
      return `${numbersOnly.slice(0, 4)}-${numbersOnly.slice(4, 6)}`;
    }
    return code; // Retorna o código original se não tiver o tamanho esperado
  };

  // Função para aplicar máscara durante a digitação
  const handleCodeChange = (e) => {
    let value = e.target.value;
    // Remove qualquer caractere que não seja número
    const numbersOnly = value.replace(/\D/g, "");
    
    // Limita a 6 dígitos
    const limitedNumbers = numbersOnly.slice(0, 6);
    
    // Aplica a formatação
    let formattedValue = limitedNumbers;
    if (limitedNumbers.length > 4) {
      formattedValue = `${limitedNumbers.slice(0, 4)}-${limitedNumbers.slice(4)}`;
    }
    
    // Validação em tempo real
    let errorMessage = "";
    if (limitedNumbers.length === 0) {
      errorMessage = "";
    } else if (limitedNumbers.length < 6) {
      errorMessage = `O código CBO deve conter exatamente 6 dígitos. Você digitou ${limitedNumbers.length} dígito(s).`;
    }
    
    setCodeError(errorMessage);
    setCurrentProfession({
      ...currentProfession,
      code: formattedValue,
    });
  };

  const fetchProfessions = useCallback(() => {
    const searchText = searchTerm !== "" ? `&search=${searchTerm}` : "";
    fetch(
      `/backend/professions/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Não foi possível carregar a lista de profissões. Tente novamente.";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setProfessions(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar profissões:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchProfessions();
  }, [fetchProfessions, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (profession) => {
    setCurrentProfession({
      ...profession,
      code: formatCode(profession.code), // Aplica a formatação ao editar
    });
    setCodeError("");
    setFieldErrors({});
    setIsEditing(true);
    setOpenModal(true);
  };

  const handleAdd = () => {
    setCurrentProfession({
      code: "",
      name: "",
    });
    setCodeError("");
    setFieldErrors({});
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCodeError("");
    setFieldErrors({});
  };

  const handleSave = () => {
    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/professions/${currentProfession.id}/`
      : "/backend/professions/";

    // Remove a formatação do código antes de enviar para o backend
    const professionToSave = {
      ...currentProfession,
      code: currentProfession.code.replace(/\D/g, ""), // Remove hífen e outros caracteres não numéricos
    };

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(professionToSave),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            code: 'code',
            name: 'name'
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            throw new Error("Por favor, corrija os erros nos campos destacados.");
          } else {
            throw new Error("Não foi possível salvar a profissão. Verifique os dados e tente novamente.");
          }
        } catch (e) {
          // Se não conseguir parsear o JSON, usa a mensagem padrão
          if (e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Não foi possível salvar a profissão. Verifique os dados e tente novamente.");
        }
      })
      .then((data) => {
        if (isEditing) {
          setProfessions((prevProfessions) =>
            prevProfessions.map((profession) =>
              profession.id === data.id ? data : profession
            )
          );
        } else {
          setProfessions((prevProfessions) => [...prevProfessions, data]);
        }
        alert("Profissão cadastrada com sucesso!");
        setOpenModal(false);
        setFieldErrors({});
        setCodeError("");
        fetchProfessions();
      })
      .catch((error) => {
        console.error("Erro ao salvar profissão:", error);
        alert(error.message);
      });
  };

  const handleDelete = (professionId) => {
    if (window.confirm("Deseja realmente excluir esta profissão? Esta ação não pode ser desfeita.")) {
      fetch(`/backend/professions/${professionId}/`, {
        method: "DELETE",
      })
        .then(async (response) => {
          if (response.ok) {
            setProfessions((prevProfessions) =>
              prevProfessions.filter((profession) => profession.id !== professionId)
            );
            alert("Profissão removida com sucesso!");
            fetchProfessions();
          } else {
            // Tenta extrair a mensagem de erro específica do backend
            let errorMessage = "Não foi possível excluir a profissão. Verifique se não há vínculos com outros registros.";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              // Se não conseguir parsear o JSON, usa a mensagem padrão
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir profissão:", error);
          alert(error.message);
        });
    }
  };

  const handleDownloadTemplate = () => {
    window.open("/backend/professions/csv-template/", "_blank");
  };

  const handleOpenImportModal = () => {
    setOpenImportModal(true);
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleCloseImportModal = () => {
    setOpenImportModal(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        alert("Por favor, selecione um arquivo CSV válido. O arquivo deve ter a extensão .csv");
        event.target.value = "";
      }
    }
  };

  const handleImportCSV = async () => {
    if (!selectedFile) {
      alert("Por favor, selecione um arquivo CSV antes de importar.");
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("csv_file", selectedFile);

    try {
      const response = await fetch("/backend/professions/import-csv/", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok || response.status === 207) {
        setImportResult(result);
        fetchProfessions(); // Recarregar a lista
      } else {
        throw new Error(result.error || "Não foi possível importar o arquivo CSV. Verifique o formato do arquivo e tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao importar CSV:", error);
      setImportResult({
        success: false,
        error: error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de profissões"
        actions={[
          <Tooltip title="Baixar template CSV">
            <AddButton onClick={handleDownloadTemplate}>
              <IconDownload stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
              Template CSV
            </AddButton>
          </Tooltip>,
          ...(canImportData ? [
          <Tooltip key="import-csv" title="Importar profissões via CSV">
            <AddButton onClick={handleOpenImportModal}>
              <IconUpload stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
              Importar CSV
            </AddButton>
          </Tooltip>,
          ] : []),
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar profissão
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={setSearchTerm} />
        <Divider sx={{ my: 2 }} />
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>Código CBO</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {professions.map((profession) => (
              <TableRow key={profession.id}>
                <TableCell>{formatCode(profession.code)}</TableCell>
                <TableCell>{profession.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(profession)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(profession.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Profissões por página"
      />

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? "Editar Profissão" : "Adicionar Profissão"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código CBO"
                value={currentProfession.code}
                onChange={(e) => {
                  handleCodeChange(e);
                  // Limpa o erro do backend quando o usuário começa a digitar
                  if (fieldErrors.code) {
                    setFieldErrors({ ...fieldErrors, code: null });
                  }
                }}
                required
                placeholder="Ex: 2412-05"
                error={!!codeError || !!fieldErrors.code}
                helperText={fieldErrors.code || codeError || "Digite o código CBO no formato 2412-05 (6 dígitos numéricos)"}
                inputProps={{
                  maxLength: 7, // 4 dígitos + hífen + 2 dígitos
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nome da Profissão"
                value={currentProfession.name}
                onChange={(e) => {
                  setCurrentProfession({
                    ...currentProfession,
                    name: e.target.value,
                  });
                  // Limpa o erro do campo quando o usuário começa a digitar
                  if (fieldErrors.name) {
                    setFieldErrors({ ...fieldErrors, name: null });
                  }
                }}
                required
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Importação CSV */}
      <Dialog open={openImportModal} onClose={handleCloseImportModal} maxWidth="md" fullWidth>
        <DialogTitle>Importar Profissões via CSV</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Faça upload de um arquivo CSV contendo as colunas: <strong>CODIGO</strong> e <strong>TITULO</strong> (aceita com ou sem acentuação). O código deve ter 6 dígitos numéricos.
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                startIcon={<IconDownload />}
                sx={{ mr: 2 }}
              >
                Baixar Template
              </Button>
              <Typography variant="caption" color="textSecondary">
                Baixe o template para verificar o formato correto do arquivo
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              ref={fileInputRef}
              style={{ display: "none" }}
            />
            
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<IconUpload />}
              sx={{ mb: 2 }}
            >
              Selecionar Arquivo CSV
            </Button>

            {selectedFile && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Arquivo selecionado: <strong>{selectedFile.name}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Tamanho: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}

            {importing && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Importando arquivo...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResult && (
              <Box sx={{ mt: 2 }}>
                {importResult.success ? (
                  <Alert severity={importResult.total_errors > 0 ? "warning" : "success"}>
                    <Typography variant="body2">
                      <strong>Importação concluída!</strong>
                    </Typography>
                    <Typography variant="body2">
                      • Criadas: {importResult.created} profissões
                    </Typography>
                    <Typography variant="body2">
                      • Atualizadas: {importResult.updated} profissões
                    </Typography>
                    {importResult.total_errors > 0 && (
                      <Typography variant="body2">
                        • Erros: {importResult.total_errors} linhas
                      </Typography>
                    )}
                  </Alert>
                ) : (
                  <Alert severity="error">
                    <Typography variant="body2">
                      <strong>Erro ao importar o arquivo:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {importResult.error}
                    </Typography>
                    {importResult.found_headers && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          <strong>Cabeçalhos encontrados:</strong> {importResult.found_headers.join(', ')}
                        </Typography>
                        {importResult.cleaned_headers && (
                          <Typography variant="caption" display="block">
                            <strong>Cabeçalhos limpos:</strong> {importResult.cleaned_headers.join(', ')}
                          </Typography>
                        )}
                        {importResult.debug_info && (
                          <Typography variant="caption" display="block">
                            <strong>Debug:</strong> {importResult.debug_info}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Alert>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: "auto" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Detalhes dos erros encontrados:</strong>
                    </Typography>
                    {importResult.errors.map((error, index) => (
                      <Typography key={index} variant="caption" color="error" display="block">
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportModal} color="secondary">
            Fechar
          </Button>
          <Button
            onClick={handleImportCSV}
            color="primary"
            disabled={!selectedFile || importing}
            startIcon={importing ? null : <IconUpload />}
          >
            {importing ? "Importando..." : "Importar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProfessionsList;
